import { Messages, Product } from '../../@types/wishlist.js';
import { FilterOptionState } from './filter-option-state.js';
import { OptionUIModel } from './option-ui-model.js';
import { createLabel } from './filter-label.js';
import { FilterAction } from './filter-action.js';
import { SortAction } from './sort-action.js';

export class FilterOption implements OptionUIModel {
  readonly id: string;

  readonly groupId?: string;

  private state: FilterOptionState;

  private editState?: FilterOptionState;

  readonly action: SortAction | FilterAction;

  readonly isDefault: boolean;

  readonly isOneClick: boolean;

  readonly predefinedLabel?: string;

  private readonly shortLabelMsgKey?: string;

  public constructor(
    id: string,
    action: SortAction | FilterAction,
    predefinedLabel?: string,
    groupId?: string,
    isDefault: boolean = false,
    isOneClick: boolean = false,
    shortLabelMsgKey?: string,
  ) {
    this.id = id;
    this.action = action;
    this.state = new FilterOptionState(isDefault);
    this.editState = undefined;
    this.isDefault = isDefault;
    this.isOneClick = isOneClick;
    this.predefinedLabel = predefinedLabel;
    this.groupId = groupId;
    this.shortLabelMsgKey = shortLabelMsgKey;
  }

  public equalsId(otherId: string) {
    return this.id === otherId;
  }

  public label(messages: Messages): string {
    return this.predefinedLabel ?? createLabel(messages, 'ents_wishlist_filter_option_', this.id);
  }

  public shortLabel(messages: Messages): string {
    return this.shortLabelMsgKey ? (messages[this.shortLabelMsgKey] ?? this.label(messages)) : this.label(messages);
  }

  public isEdited(): boolean {
    return this.editState !== undefined;
  }

  public applyEditState(): void {
    if (this.editState) {
      this.state = this.editState.copy();
      this.undoEditState();
    }
  }

  public undoEditState(): void {
    this.editState = undefined;
  }

  public isActive(includeEditState: boolean = true) {
    if (includeEditState && this.editState) {
      return this.editState.isActive();
    }
    return this.state.isActive();
  }

  public toggleActivationState() {
    this.editState ??= this.state.copy();
    this.editState.toggleActivationState();
    if (this.state.isActive() === this.editState?.isActive()) {
      this.undoEditState();
    }
  }

  public getCount(): number {
    return this.editState ? this.editState.getCount() : this.state.getCount();
  }

  public getProductScoreSum(): number {
    return this.editState ? this.editState.getProductScoreSum() : this.state.getProductScoreSum();
  }

  public resetCount() {
    if (this.editState) {
      return this.editState.resetCount();
    }
    return this.state.resetCount();
  }

  public setCount(count: number) {
    this.state.setCount(count);
    if (this.editState) {
      this.editState.setCount(count);
    }
  }

  public setCountAndScore(count: number, scoreSum: number) {
    this.state.setCountAndScore(count, scoreSum);
    if (this.editState) {
      this.editState.setCountAndScore(count, scoreSum);
    }
  }

  public countProduct(product: Product, isScoringEnabled: boolean) {
    if (this.action instanceof FilterAction && !this.action.filterStrategy(product)) {
      return;
    }

    const productScore = isScoringEnabled ? this.calculateProductScore(product) : 0;
    this.state.incCount(productScore);
    if (this.editState) {
      this.editState.incCount(productScore);
    }
  }

  public calculateProductScore(product: Product): number {
    let score = 0.1;

    if (
      product.productLine ||
      product.capsule ||
      product.mixAndMatchMessage ||
      product.limitedMessage ||
      product.exclusiveMessage ||
      product.certifiedMessage ||
      product.newMessage
    ) {
      score += 0.05;
    }

    const daysSinceAdded = (Date.now() - Date.parse(product.added)) / (1000 * 60 * 60 * 24);
    // Aktualitätswert zwischen 0 und 1, exponentiell abnehmend
    score += Math.exp(-daysSinceAdded / 14) * 0.5;

    if (product.price?.isReduced || product.isAnyArticleInPromotion) {
      score += 0.3;
    }

    if (product.collectionReferences.length > 0) {
      score += 0.1;
    }

    if (product.articles.some((a) => a.lowStockMessage !== undefined)) {
      score += 0.1;
    }

    if (product.isOutOfStock) {
      score -= 0.3;
    }

    return Math.max(score, 0);
  }

  public reset() {
    if (this.isActive(true)) {
      this.toggleActivationState();
    }
  }
}
